import { cx } from "@emotion/css";
import { Globe, Lock } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useFontSize } from "src/hooks/useFontSize";

import TextArea from "../../../Default/TextArea";

interface PermissionsFiltersProps {
	permission: string;
	setPermission: (permission: string) => void;
	disabled?: boolean;
	className?: string;
}

export const PermissionsFilters = ({
	setPermission,
	permission,
	disabled = false,
	className,
}: PermissionsFiltersProps) => {
	const { t } = useTranslation();
	const { modalTextFontSizeClass, modalHeaderFontSizeClass } = useFontSize();

	const options = [
		{
			value: "public",
			label: t("filters.decks.permission.public", "Publiczny"),
			description: t(
				"filters.decks.permission.publicDescription",
				"Wszyscy mogą zobaczyć Twój zestaw i uczyć się z niego, ale tylko Ty możesz go edytować.",
			),
			testId: "permission-public",
			Icon: Globe,
		},
		{
			value: "private",
			label: t("filters.decks.permission.private", "Prywatny"),
			description: t(
				"filters.decks.permission.privateDescription",
				"Tylko Ty masz dostęp do tego zestawu — pozostali użytkownicy go nie zobaczą ani nie będą mogli się z niego uczyć.",
			),
			testId: "permission-private",
			Icon: Lock,
		},
	];

	return (
		<div
			className={cx("flex gap-8 flex-col-mobile w-100", className, {
				"opacity-50 pointer-events-none": disabled,
			})}
		>
			{options.map(({ value, label, description, testId, Icon }) => (
				<div
					key={value}
					className={cx(
						"permission-tile flex items-start gap-16 p-20 border-radius-16 bg-white cursor-pointer flex-1",
						{
							"permission-tile--selected": permission === value,
						},
					)}
					onClick={() => !disabled && setPermission(value)}
					data-test-id={testId}
				>
					<span className="permission-tile--icon flex-center-row border-light-grey border-radius-8 bg-white flex-shrink-0">
						<Icon className="icon-small icon-primary" />
					</span>
					<span className="flex flex-col gap-4">
						<span className={cx(modalHeaderFontSizeClass, "text-black")}>
							{label}
						</span>
						<span className={cx(modalTextFontSizeClass, "text-dark-grey")}>
							{description}
						</span>
					</span>
				</div>
			))}
		</div>
	);
};

interface DescriptionAreaProps {
	description: string;
	setDescription: (description: string) => void;
	label?: string;
	className?: string;
	error?: string;
}

export const DescriptionArea = ({
	description,
	setDescription,
	label,
	className,
	error,
}: DescriptionAreaProps) => {
	const { t } = useTranslation();
	return (
		<TextArea
			className={className}
			label={error || label || t("modals.edit.deck.description")}
			value={description}
			multiline
			rows={3}
			onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
				setDescription(e.target.value)
			}
			error={!!error}
		/>
	);
};
